import React, { useMemo } from 'react';
import { Table, Checkbox, TextInput, Select, Pagination, Loader, Group, Text, Paper, ActionIcon } from '@mantine/core';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { useCampaign } from '../../context/CampaignContext';
import { useClient } from '../../context/ClientContext';
import moment from 'moment';

const AGE_GROUPS = [
  { label: 'Under 25', value: 'under25' },
  { label: '25-34', value: '25-34' },
  { label: '35-44', value: '35-44' },
  { label: '45-54', value: '45-54' },
  { label: '55-64', value: '55-64' },
  { label: '65+', value: '65plus' },
];

const PAGE_SIZE = 10;

function ClientSelectionTable({loading}) {
  const {
    selectedClientIds, setSelectedClientIds,
    clientSearch: search, setClientSearch: setSearch,
    genderFilter, setGenderFilter,
    ageGroupFilter, setAgeGroupFilter,
    clientPage: page, setClientPage: setPage,
    sorting, setSorting, processedClients
  } = useCampaign();

  const audienceSize = selectedClientIds.length;
  const { clients } = useClient();

  // Pagination
  const totalPages = Math.ceil(processedClients.length / PAGE_SIZE) || 1;
  const pagedClients = processedClients.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sorting.field !== field) return null;
    return sorting.direction === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
  };

  const handleCheck = (id) => {
    if (selectedClientIds.includes(id)) {
      setSelectedClientIds(selectedClientIds.filter(cid => cid !== id));
    } else {
      setSelectedClientIds([...selectedClientIds, id]);
    }
  };

  const allChecked = pagedClients.length > 0 && pagedClients.every(c => selectedClientIds.includes(c.id));
  const someChecked = pagedClients.some(c => selectedClientIds.includes(c.id));

  const handleCheckAll = () => {
    if (allChecked) {
      setSelectedClientIds(selectedClientIds.filter(id => !pagedClients.some(c => c.id === id)));
    } else {
      const newIds = [...new Set([...selectedClientIds, ...pagedClients.map(c => c.id)])];
      setSelectedClientIds(newIds);
    }
  };

  const genderData = useMemo(() => {
    const allClientGenders = clients
      ?.filter(client => client?.gender)
      .map(client => client?.gender);

    const deduplicatedGenders = [...new Set(allClientGenders)].map(gender => ({ value: gender, label: gender }));
    return [{ value: '', label: 'All Genders' }, ...deduplicatedGenders];
  }, [clients]);

  return (
    <>
      <Group mb="sm">
        <TextInput
          placeholder="Search by name, email, or phone"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <Select
          placeholder="Gender"
          data={genderData}
          value={genderFilter}
          onChange={setGenderFilter}
          style={{ width: 140 }}
        />
        <Select
          placeholder="Age Group"
          data={[{ value: '', label: 'All Ages' }, ...AGE_GROUPS]}
          value={ageGroupFilter}
          onChange={setAgeGroupFilter}
          style={{ width: 140 }}
        />
      </Group>
      {loading ? <Loader /> : (
        <>
          <Paper withBorder radius="md" shadow="xs" p={0}>
            <Table striped highlightOnHover withBorder verticalSpacing="md" horizontalSpacing="md" style={{ borderRadius: 12, overflow: 'hidden' }}>
              <Table.Thead style={{ background: '#f5f3ff' }}>
                <Table.Tr>
                  <Table.Th style={{ fontWeight: 700, textAlign: 'center', verticalAlign: 'middle' }}>
                    <Checkbox checked={allChecked} indeterminate={!allChecked && someChecked} onChange={handleCheckAll} />
                  </Table.Th>
                  <Table.Th 
                    style={{ fontWeight: 700, verticalAlign: 'middle', cursor: 'pointer' }}
                    onClick={() => handleSort('last_name')}
                  >
                    <Group position="apart">
                      <span>Name</span>
                      {getSortIcon('last_name')}
                    </Group>
                  </Table.Th>
                  <Table.Th 
                    style={{ fontWeight: 700, verticalAlign: 'middle', cursor: 'pointer' }}
                    onClick={() => handleSort('last_appointment')}
                  >
                    <Group position="apart">
                      <span>Last Appointment</span>
                      {getSortIcon('last_appointment')}
                    </Group>
                  </Table.Th>
                  <Table.Th 
                    style={{ fontWeight: 700, verticalAlign: 'middle', cursor: 'pointer' }}
                    onClick={() => handleSort('last_service')}
                  >
                    <Group position="apart">
                      <span>Last Service</span>
                      {getSortIcon('last_service')}
                    </Group>
                  </Table.Th>
                  <Table.Th 
                    style={{ fontWeight: 700, verticalAlign: 'middle', cursor: 'pointer' }}
                    onClick={() => handleSort('birthday')}
                  >
                    <Group position="apart">
                      <span>Age</span>
                      {getSortIcon('birthday')}
                    </Group>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pagedClients.length === 0 ? (
                  <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center">No clients found</Text></Table.Td></Table.Tr>
                ) : pagedClients.map(client => (
                  <Table.Tr 
                    key={client.id} 
                    style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.background='#f3f4f6'} 
                    onMouseOut={e => e.currentTarget.style.background='' }
                  >
                    <Table.Td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <Checkbox checked={selectedClientIds.includes(client.id)} onChange={() => handleCheck(client.id)} />
                    </Table.Td>
                    <Table.Td style={{ verticalAlign: 'middle' }}>{client.first_name} {client.last_name}</Table.Td>
                    <Table.Td style={{ verticalAlign: 'middle' }}>
                      {client.last_appointment ? moment(client.last_appointment).format('MMM D, YYYY') : '-'}
                    </Table.Td>
                    <Table.Td style={{ verticalAlign: 'middle' }}>
                      {client.last_service_name || client.last_service || '-'}
                    </Table.Td>
                    <Table.Td style={{ verticalAlign: 'middle' }}>
                      {client.birthday ? moment().diff(client.birthday, 'years') : '-'}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          <Group mt="sm" justify="space-between" align="center">
            <Text>Audience size: <b>{audienceSize}</b></Text>
            <Pagination page={page} onChange={setPage} total={totalPages} size="md" radius="md" />
          </Group>
        </>
      )}
    </>
  );
}

export default ClientSelectionTable; 